// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  CreateTimesheetFieldOptionRequestSchema,
  ListTimesheetFieldOptionsRequestSchema,
  ListTimesheetFieldsRequestSchema,
  TimesheetFieldOption,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type CreateTimesheetFieldRpc = (request: Init<typeof CreateTimesheetFieldOptionRequestSchema>) => Observable<TimesheetFieldOption>

export const createTimesheetFieldOption = (createTimesheetFieldOptionRpc: CreateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimesheetFieldOption.match),
      map(action => action.payload),
      mergeMap(payload =>
        createTimesheetFieldOptionRpc(payload.request).pipe(
          mergeMap((response) => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return of(Slice.addTimesheetFieldOption({
              option: response,
              timesheetFieldId: payload.request.fieldId ?? "",
            }))
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<Init< typeof CreateTimesheetFieldOptionRequestSchema>>): Observable<Action> => {
  if (payload.onError) {
    payload.onError()
  }
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: create(ListTimesheetFieldsRequestSchema, {
            siteId: payload.request.siteId,
          }),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.FailedPrecondition) {
      return concat([
        Actions.listTimesheetFieldOptions(
          create(ListTimesheetFieldOptionsRequestSchema, {
            siteId: payload.request.siteId,
            timesheetFieldId: payload.request.fieldId,
          })
        ),
        showSnackBar({
          message: t({
            id: "msg_create_timesheet_field_option_failed_precondition",
            message: "Failed to create timesheet field option. Duplicate label or value.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_create_timesheet_field_option_permission_denied",
          message: "Failed to create timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_create_timesheet_field_option_failed",
      message: "Failed to create timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const createTimesheetFieldOptionRpc = (request: Init<typeof CreateTimesheetFieldOptionRequestSchema>): Observable<TimesheetFieldOption> =>
  executorMutation(() => timesheetFieldService.createTimesheetFieldOption(create(CreateTimesheetFieldOptionRequestSchema, request)))

export default createTimesheetFieldOption(createTimesheetFieldOptionRpc)
